import { useMutation, useQuery } from '@apollo/client';
import { Loader } from '@teamsnap/teamsnap-ui';
import { DateTime } from 'luxon';
import * as React from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { v4 as uuidv4 } from 'uuid';

// Seperate local imports from dependency imports
import api from 'core/api';
import * as CreateFormField from 'core/gql/mutations/create-form-field';
import * as CreateFormMessage from 'core/gql/mutations/create-form-message';
import * as CreateOrUpdateFormPartnerOfferings from 'core/gql/mutations/create-or-update-partner-offerings';
import * as CreateParticipantGroups from 'core/gql/mutations/create-participant-groups';
import * as CreateRegistration from 'core/gql/mutations/create-registration';
import * as CreateWaiver from 'core/gql/mutations/create-waiver';
import * as DeleteFormField from 'core/gql/mutations/delete-form-field';
import * as UpdateFormField from 'core/gql/mutations/update-form-field';
import * as UpdateFormMessage from 'core/gql/mutations/update-form-message';
import * as UpdateParticipantGroups from 'core/gql/mutations/update-participant-groups';
import * as UpdateRegistration from 'core/gql/mutations/update-registration';
import * as UpdateWaiver from 'core/gql/mutations/update-waiver';
import * as GqlRegistrations from 'core/gql/queries/get-all-forms-reg';
import * as GqlOrganizationFormFieldDefinitions from 'core/gql/queries/get-organization-form-field-definitions';
import * as ListPaymentAccounts from 'core/gql/queries/list-payment-accounts';
import * as ProgramSeasonsDivisions from 'core/gql/queries/program-season-divisions';
import * as RegistrationFormAdjustments from 'core/gql/queries/registration-form-adjustments';
import * as GetForm from 'core/gql/queries/show-form';

import { sortByAlpha } from 'core/utils';
import { Stepper, StepperSubmitCallbackParams } from 'frontend-toolkit';
import { EnhancedDateTimePicker, WaiverForm } from './';
import {
  convertTimeToDisplayString,
  convertToUTC,
  excludeCurrentDayPastTimes,
  excludeDaysUntilStartDate,
  excludeStartDatePastTimes,
  getOrgTimezone,
  getOrgTimezoneOffset,
} from './date-utils';
import {
  createFormRequest,
  createParticipantGroupRequest,
  handleCreateOrUpdatePartnerOfferingsRequest,
  handleFormFieldsRequest,
  handleFormMessageRequest,
  handleFormWaiversRequest,
  updateFormRequest,
  updateParticipantGroupRequest,
} from './serverRequests';
import validationSchema from './validationSchema';

import { trackChurnZeroEvent } from 'components/common/useChurnZero';
import ConfirmationMessageForm from './ConfirmationMessageForm';
import GroupEligibility from './GroupEligibility';
import GroupFees from './GroupFees';
import MerchantAccountSelect from './MerchantAccountSelect';
import ParticipantGroups from './ParticipantGroups';
import * as Types from './types';

import Questions, {
  buildDefaultCheckedFields,
  hydrateFormFields,
  orderFieldsByTarget,
} from 'components/registration/v2/Questions';

import { Icon, Tooltip } from '@teamsnap/snap-ui';
import Breadcrumb, { BreadcrumbSection } from 'components/common/breadcrumb';
import ENVIRONMENT from 'core/environment';
import { FeatureFlagConfig, FeatureFlags } from 'core/feature-flags';
import { FeatureFlagService } from 'frontend-toolkit';
import { useAmplitude } from 'hooks/amplitude';
import { OnSubmit, useForm } from 'react-hook-form';
import {
  buildCapacityLimits,
  buildEligibilityRules,
  formatFeesForSummary,
  formatGroupsForSummary,
} from '../utils';
import Addons from './Addons';
import DepositAccountWarningModal from './DepositAccountWarningModal/DepositAccountWarningModal';
import Discounts from './Discounts';
import GroupCapacity from './GroupCapacity';
import NgbIntegrationStep, {
  useNgbIntregrationDefaultAssignment,
} from './NgbIntegrationStep/NgbIntegrationStep';
import {
  ReactHookFormInputs,
  RegistrationContext,
} from './RegistrationContext';
import UnapprovedMerchantAccountModal from './UnapprovedMerchantAccountModal';
import useApiGetNgbIntegrations from './hooks/useApiGetNgbIntegrations';
import PaymentPlans from './PaymentPlans';

const Registration: React.FunctionComponent<Types.RegistrationFormProps> = ({
  orgId,
  isModalOpen,
  setIsModalOpen,
  prevPath,
  org,
}) => {
  // NOTE: Feature flag for participant group restrictions needs to be removed once the feature is fully released
  const isParticipantGroupRestrictionEnabled =
    FeatureFlagService.isFeatureEnabledForOrg(
      FeatureFlagConfig[FeatureFlags.PARTICIPANT_GROUP_RESTRICTION],
      ENVIRONMENT,
      orgId
    );

  // NOTE: Feature flag for capacity limits needs to be removed once the feature is fully released
  const isCapacityLimitsEnabled = FeatureFlagService.isFeatureEnabledForOrg(
    FeatureFlagConfig[FeatureFlags.PARTICIPANT_GROUP_CAPACITY_LIMIT],
    ENVIRONMENT,
    orgId
  );


  // NOTE: Feature flag for coupon codes needs to be removed once the feature is fully released
  const isCouponCodesFeatureEnabled = FeatureFlagService.isFeatureEnabledForOrg(
    FeatureFlagConfig[FeatureFlags.COUPON_CODES],
    ENVIRONMENT,
    +orgId
  );

  // NOTE: Feature flag for Auto Rostering needs to be removed once the feature is fully released
  const autoRosteringIsEnabled = FeatureFlagService.isFeatureEnabledForOrg(
    FeatureFlagConfig[FeatureFlags.AUTO_ROSTERING],
    ENVIRONMENT,
    orgId
  );

  const isAddonsFeatureEnabled = FeatureFlagService.isFeatureEnabledForOrg(
    FeatureFlagConfig[FeatureFlags.ADD_ONS],
    ENVIRONMENT,
    +orgId
  );

  const isHCRFeatureEnabled = FeatureFlagService.isFeatureEnabledForOrg(
    FeatureFlagConfig[FeatureFlags.HCR_REG_2],
    ENVIRONMENT,
    +orgId
  );

  const history = useHistory();
  const params = useParams<{ formId: string; step: string }>();
  const { amplitudeTrack } = useAmplitude();
  const { data } = useQuery(ListPaymentAccounts.QUERY, {
    variables: { scopeId: +orgId, scopeType: 'organization' },
    errorPolicy: 'all',
  });

  // Defaults
  const registrationDefaults: Types.RegistrationType = {
    name: {
      value: '',
      touched: true,
    },
    program: {
      id: null,
      value: '',
      touched: true,
    },
    season: {
      id: null,
      value: '',
      touched: true,
    },
    paymentAccount: {
      value: '',
      touched: true,
    },
  };

  const formFeesDefaults: Types.FormFeesType = { touched: true, value: [] };
  const formFieldsDefault: Types.FormFieldsType = { touched: true, value: [] };
  const waiversDefault: Types.FormWaiversType = { touched: true, value: [] };
  const participantGroupsDefault: Types.ParticipantGroupsType = {
    touched: true,
    value: [],
  };
  const formMessageDefault: Types.FormMessageType = {
    touched: false,
    id: null,
    content: null,
  };
  const paymentAccountInfoDefault: Types.PaymentAccountInfo = {
    paymentAccountId: null,
    organizationPaysProcessingFee: false,
    allowOfflineCheckout: false,
  };

  const groupEligibilityDefault: Types.GroupEligibilityType = {
    value: null,
    touched: false,
  };
  const groupCapacityDefault: Types.GroupCapacityType = {
    value: null,
    touched: false,
  };

  // States
  const hasValidFormId = !Number.isNaN(parseInt(params.formId));
  const [formId, setFormId] = React.useState<number>(
    hasValidFormId ? parseInt(params.formId) : null
  );
  const [selectedProgram, setSelectedProgram] =
    React.useState<Types.RegistrationProgram>(null);
  const [selectedSeason, setSelectedSeason] =
    React.useState<Types.RegistrationSeason>(null);
  const [startDate, setStartDate] = React.useState<Date | null>(null);
  const [endDate, setEndDate] = React.useState<Date | null>(null);
  const [orgTimezone, setOrgTimezone] = React.useState<string>('');
  const [orgTimezoneIana, setOrgTimezoneIana] = React.useState<string>('');
  const [currentStep, setCurrentStep] = React.useState<number>(0);
  const [updatedGroups, setUpdatedGroups] = React.useState<
    Types.ParticipantGroup[]
  >([]);
  const [paymentAccountInfo, setPaymentAccountInfo] = React.useState(
    paymentAccountInfoDefault
  );
  const [paymentAccounts, setPaymentAccounts] = React.useState(null);
  const [currentBreadcrumb, updateCurrentBreadcrumb] =
    React.useState<Types.RegistrationSteps>(Types.RegistrationSteps.PROGRAMS);
  const [hasFees, setHasFees] = React.useState<boolean>(false);
  const [eligibilityRules, setEligibilityRules] =
    React.useState<Types.GroupEligibilityRow[]>(null);
  const [capacityLimits, setCapacityLimits] =
    React.useState<Types.GroupCapacityRow[]>(null);
  const [showDepositAccountModal, setShowDepositAccountModal] =
    React.useState(false);
  const [
    showUnapprovedMerchantAccountModal,
    setShowUnapprovedMerchantAccountModal,
  ] = React.useState(false);
  const [merchantAccountPaymentsEnabled, setMerchantAccountPaymentsEnabled] = React.useState(false);
  const [
    paymentAccountUnapprovedWithFees,
    setPaymentAccountUnapprovedWithFees,
  ] = React.useState(false);
  const [availableAddons, setAvailableAddons] = React.useState<
    Types.PartnerOffering[]
  >([]);
  const [addonState, setAddonState] =
    React.useState<Types.RegistrationAddonState[]>();

  React.useEffect(() => {
    const paymentAccount = paymentAccounts?.find(
      (account) => account.id === paymentAccountInfo.paymentAccountId
    );

    if (paymentAccount && hasFees) {
      if (
        paymentAccount.paymentsEnabled === false ||
        paymentAccount.payoutsEnabled === false
      ) {
        setPaymentAccountUnapprovedWithFees(true);
        setMerchantAccountPaymentsEnabled(paymentAccount.paymentsEnabled);
      } else {
        setPaymentAccountUnapprovedWithFees(false);
        setMerchantAccountPaymentsEnabled(paymentAccount.paymentsEnabled);
      }
    } else {
      setPaymentAccountUnapprovedWithFees(false);
      setMerchantAccountPaymentsEnabled(false);
    }
  }, [hasFees, paymentAccountInfo, paymentAccounts]);

  React.useEffect(() => {
    if (currentStep >= -1 && formId) {
      history.push(
        `/organizations/${orgId}/registration/${formId}/${currentStep}`
      );
    }
  }, [currentStep, formId, history, orgId]);

  React.useEffect(() => {
    if (data && data.listPaymentAccounts) {
      setPaymentAccounts(data.listPaymentAccounts);
    }
  }, [data]);

  // ToDo: break the state to handle it separately
  const [state, setState] = React.useState<Types.State>({
    registration: registrationDefaults,
    dates: `${startDate} - ${endDate}`,
    fees: formFeesDefaults,
    participantGroups: participantGroupsDefault,
    formFields: formFieldsDefault,
    waivers: waiversDefault,
    message: formMessageDefault,
    groupEligibility: groupEligibilityDefault,
    groupCapacity: groupCapacityDefault,
  });

  const {
    registration,
    dates,
    fees,
    waivers,
    participantGroups,
    message,
    formFields,
    groupCapacity,
  } = state;

  // React hooks for server submission requests MUTATIONS
  const mutationOptions = {
    refetchQueries: [
      { query: GqlRegistrations.QUERY, variables: { organizationId: +orgId } },
      formId && {
        query: GetForm.QUERY,
        variables: { formId: formId, organizationId: +orgId },
      },
    ],
  };

  const refetchForm = formId && {
    refetchQueries: [
      {
        query: GetForm.QUERY,
        variables: { formId: formId, organizationId: +orgId },
      },
    ],
  };

  const [createRegistration] = useMutation(
    CreateRegistration.MUTATION,
    mutationOptions
  );
  const [updateRegistration] = useMutation(
    UpdateRegistration.MUTATION,
    mutationOptions
  );

  const [createFormField] = useMutation(CreateFormField.MUTATION, refetchForm);
  const [updateFormField] = useMutation(UpdateFormField.MUTATION, refetchForm);
  const [deleteFormField] = useMutation(DeleteFormField.MUTATION, refetchForm);

  const [createWaiver] = useMutation(CreateWaiver.MUTATION, refetchForm);
  const [updateWaiver] = useMutation(UpdateWaiver.MUTATION, refetchForm);

  const [createParticipantGroups] = useMutation(
    CreateParticipantGroups.MUTATION,
    refetchForm
  );
  const [updateParticipantGroups] = useMutation(
    UpdateParticipantGroups.MUTATION,
    refetchForm
  );

  const [createFormMessage] = useMutation(
    CreateFormMessage.MUTATION,
    refetchForm
  );
  const [updateFormMessage] = useMutation(
    UpdateFormMessage.MUTATION,
    refetchForm
  );

  const [createOrUpdateFormPartnerOfferings] = useMutation(
    CreateOrUpdateFormPartnerOfferings.MUTATION,
    mutationOptions
  );

  // React hooks for server submission requests QUERIES
  const allForms =
    useQuery(GqlRegistrations.QUERY, { variables: { organizationId: orgId } })
      ?.data?.forms || [];

  const programsListQuery =
    useQuery(ProgramSeasonsDivisions.QUERY, {
      variables: { organizationId: orgId },
    });
  const programsList = programsListQuery?.data?.programs || [];
  const programsListLoading = programsListQuery?.loading;
  const programsListError = programsListQuery?.error;

  const organizationFieldDefinitionList =
    useQuery(GqlOrganizationFormFieldDefinitions.QUERY, {
      variables: { organization_id: orgId },
    })?.data?.organizationFormFieldDefinitions || [];

  const { data: savedFormData, loading: isLoadingSavedFormData } =
    useQuery<GetForm.ShowRegistrationFormResponse>(GetForm.QUERY, {
      variables: { formId: formId, organizationId: orgId },
      skip: !formId,
      fetchPolicy: 'network-only',
    });
  const savedForm = savedFormData?.showRegistrationForm || null;

  const formAdjustments =
    useQuery(RegistrationFormAdjustments.QUERY, {
      variables: { formId: +formId },
      skip: !formId,
    })?.data?.registrationFormAdjustments || [];

  React.useEffect(() => {
    try {
      api.get<any>(`/organizations/${orgId}/offerings`).then((res) => {
        const offerings = res.data.offerings ?? [];
        setAvailableAddons(offerings);

        if (!addonState || addonState.length === 0) {
          const initialAddonState = offerings.map((o) => ({
            enabled: false,
            partnerOfferingId: o.id,
          }));
          setAddonState(initialAddonState);
        }
      });
    } catch {
      setAvailableAddons([]);
    }
  }, [orgId]);

  React.useEffect(() => {
    const formHasFees =
      (participantGroups.value.length > 0 &&
        fees.value.some(
          (fee) => Number(fee.amount.replace(/[^0-9.-]+/g, '')) > 0
        )) ||
      formAdjustments.some((adj) => Number(adj.amount) > 0);

    setHasFees(formHasFees);

    if (participantGroups.value.length > 0 && selectedSeason) {
      setEligibilityRules(
        buildEligibilityRules(
          participantGroups.value,
          getSeasonDivisionsTeams(),
          selectedSeason?.id !== savedForm?.season?.id
        )
      );
      setCapacityLimits(buildCapacityLimits(participantGroups.value));
    }
  }, [participantGroups, fees, formAdjustments, selectedSeason, savedForm]);

  React.useEffect(() => {
    if (org) {
      setOrgTimezone(org.timezone);
      setOrgTimezoneIana(org.timezoneIana);
    }
  }, [org]);

  React.useEffect(() => {
    if (hasValidFormId) {
      setFormId(parseInt(params.formId));
      setCurrentStep(params.step && parseInt(params.step));
      updateCurrentBreadcrumb(
        (params.step && parseInt(params.step)) ??
          Types.RegistrationSteps.PROGRAMS
      );
    }
  }, [hasValidFormId, params]);

  React.useEffect(() => {
    if (isModalOpen && org && selectedProgram && selectedSeason) {
      amplitudeTrack.registrationWizardViewed({
        organization_id: String(org.id),
        organization_name: org.name,
        program_id: String(selectedProgram.id),
        season_id: String(selectedSeason.id),
      });
    }
  }, [isModalOpen, org, selectedProgram, selectedSeason]);

  React.useEffect(() => {
    if (isModalOpen === true) {
      if (formId && params.step) {
        history.replace(
          `/organizations/${orgId}/registration/${formId}/${params.step}`
        );
      } else if (formId) {
        history.replace(`/organizations/${orgId}/registration/${formId}`);
      } else if (!hasValidFormId) {
        history.replace(`/organizations/${orgId}/registration/new`);
      }
    } else if (prevPath) {
      history.replace(prevPath);
    } else {
      history.replace(`/organizations/${orgId}/registration`);
    }
  }, [
    isModalOpen,
    formId,
    history,
    orgId,
    hasValidFormId,
    params.step,
    prevPath,
  ]);

  React.useEffect(() => {
    if (savedForm) {
      const formatSavedDate = (savedForm) => {
        try {
          const { opened_at: openedAt, closed_at: closedAt } = savedForm;
          const orgTimezoneUTCOffset = getOrgTimezoneOffset(orgTimezoneIana);
          if (openedAt && closedAt) {
            const formattedOrgStartTime = convertTimeToDisplayString(
              openedAt,
              orgTimezoneIana
            );

            const formattedOrgCloseTime = convertTimeToDisplayString(
              closedAt,
              orgTimezoneIana
            );

            setStartDate(new Date(formattedOrgStartTime));
            setEndDate(new Date(formattedOrgCloseTime));

            return `${formattedOrgStartTime} - ${formattedOrgCloseTime}`;
          }
          if (openedAt && !closedAt) {
            const orgStartTime = DateTime.fromISO(openedAt, {
              setZone: true,
            }).minus({ hours: orgTimezoneUTCOffset });
            const formattedOrgStartTime = orgStartTime
              .setLocale('en-US')
              .toLocaleString(DateTime.DATETIME_SHORT);
            setStartDate(new Date(formattedOrgStartTime));
            setEndDate(null);
            return `Opens ${formattedOrgStartTime}`;
          }

          if (!openedAt && !closedAt) {
            setStartDate(null);
            setEndDate(null);
          }

          return 'None Added';
        } catch (e) {
          console.error(`registration::formatSavedDate(): ${e}`);
        }
      };

      setState((prevState) => ({
        ...prevState,
        registration: {
          program: {
            ...prevState.registration.program,
            ...{
              value: savedForm?.season?.program?.name,
              id: savedForm?.season?.program?.id,
            },
          },
          season: {
            ...prevState.registration.season,
            ...{ value: savedForm?.season?.name, id: savedForm?.season?.id },
          },
          name: {
            ...prevState.registration.name,
            ...{ value: savedForm?.name },
          },
          paymentAccount: {
            ...prevState.registration.paymentAccount,
            ...{
              value: paymentAccounts?.find(
                (account) => account.id === savedForm?.paymentAccountId
              )?.nickname,
            },
          },
        },
        dates: `${formatSavedDate(savedForm)}`,
      }));

      if (savedForm.participant_groups.length > 0) {
        setState((prevState) => ({
          ...prevState,
          participantGroups: {
            ...prevState.participantGroups,
            ...{ value: savedForm?.participant_groups },
          },
        }));
        const groupFees = savedForm.participant_groups.map((group) => ({
          amount: (group?.participant_fee / 100).toFixed(2).toString(),
          id: group.id,
        }));
        setState((prevState) => ({
          ...prevState,
          fees: {
            ...prevState.fees,
            ...{ value: groupFees },
          },
        }));
      }

      if (organizationFieldDefinitionList.length > 0) {
        if (savedForm.fields.length > 0) {
          setState((prevState) => ({
            ...prevState,
            formFields: {
              touched: prevState.formFields.touched,
              value: hydrateFormFields(
                savedForm.fields,
                organizationFieldDefinitionList
              ),
            },
          }));
        } else {
          const defaultCheckedFields = orderFieldsByTarget(
            buildDefaultCheckedFields(organizationFieldDefinitionList)
          );
          setState((prevState) => ({
            ...prevState,
            formFields: {
              touched: prevState.formFields.touched,
              value: defaultCheckedFields,
            },
          }));
        }
      }
      if (savedForm.waivers.length > 0) {
        setState((prevState) => ({
          ...prevState,
          waivers: {
            ...prevState.waivers,
            value: savedForm?.waivers.map((x) => ({
              ...x,
              uuid: x.uuid || uuidv4(),
            })),
          },
        }));
      }
      if (savedForm.formMessage) {
        setState((prevState) => ({
          ...prevState,
          message: {
            ...prevState.message,
            ...{
              id: savedForm?.formMessage.id,
              content: savedForm?.formMessage.content,
            },
          },
        }));
      }

      if (savedForm.partnerOfferings && savedForm.partnerOfferings.length > 0) {
        const minAddonState = savedForm.partnerOfferings.map((o) => ({
          enabled: o.enabled,
          partnerOfferingId: o.partnerOfferingId,
        }));

        setAddonState(minAddonState);
      }

      setPaymentAccountInfo({
        paymentAccountId: savedForm?.paymentAccountId,
        organizationPaysProcessingFee: savedForm?.organizationPaysProcessingFee,
        allowOfflineCheckout: savedForm?.allowOfflineCheckout,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    savedForm,
    orgTimezoneIana,
    paymentAccounts,
    organizationFieldDefinitionList,
  ]);

  React.useEffect(() => {
    setSelectedProgram(
      programsList?.find(
        (program) =>
          program.id === registration.program.id &&
          program.name === registration.program.value
      )
    );
  }, [programsList, registration.program]);

  React.useEffect(() => {
    setSelectedSeason(
      selectedProgram?.seasons?.find(
        (season) =>
          season.id === registration.season.id &&
          season.name === registration.season.value
      )
    );
  }, [selectedProgram, registration.season]);

  React.useEffect(() => {
    registration.paymentAccount.value = paymentAccounts?.find(
      (a) => a.id === paymentAccountInfo.paymentAccountId
    )?.nickname;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paymentAccountInfo, paymentAccounts]);

  React.useEffect(() => {
    setPaymentAccountInfo(paymentAccountInfoDefault);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formId]);

  React.useEffect(() => {
    handleParticipantGroupSubmission(formId, participantGroups.value);
  }, [participantGroups.value]);

  const handleEventChange = (event, registrationObj) => {
    try {
      const { target } = event;
      const { name, value, id } = target;
      const newRegObj = {
        ...registration,
        ...{
          [Object.keys(registrationObj)[0]]: {
            ...registration[Object.keys(registrationObj)[0]],
            ...{ value: registrationObj[Object.keys(registrationObj)[0]] },
          },
        },
      };
      // Check if the value on the event for the field name is different than the returned value
      // this will be set when the user manually changed the name
      if (name === 'name' && target.value !== registrationObj[name]) {
        newRegObj['name'] = { ...newRegObj['name'], ...{ value: value } };
      }
      // Set or update name if season has been changed/selected before being saved
      if (name === 'season') {
        newRegObj['season'] = { ...newRegObj['season'], ...{ id: id } };
        newRegObj['name'] = {
          ...registration['name'],
          ...{
            value: `${registration.program.value} ${registrationObj[name]}`,
          },
        };
      }
      // Always reset season when program is selected or changed
      if (name === 'program') {
        newRegObj['program'] = { ...newRegObj['program'], ...{ id: id } };
        newRegObj['season'] = {
          ...registration['season'],
          ...{ value: '', id: null },
        };
        newRegObj['name'] = { ...registration['name'], ...{ value: '' } };
      }

      handleStateChange('registration', newRegObj);
    } catch (e) {
      console.error(`registration::handleEventChange(): ${e}`);
    }
  };

  const handleUnapprovedMerchantAccountModalSubmit = () => {
    setShowUnapprovedMerchantAccountModal(false);
    setCurrentStep(Types.RegistrationSteps.DEPOSIT_ACCOUNT);
  };

  const handleUnapprovedMerchantAccountModalClose = async () => {
    setShowUnapprovedMerchantAccountModal(false);

    const variables = merchantAccountPaymentsEnabled ?
    {
      name: registration.name.value,
      seasonId: selectedSeason?.id,
    }:
    {
      name: registration.name.value,
      seasonId: selectedSeason?.id,
      openedAt: null,
      closedAt: null,
    };


    await updateFormRequest(variables, formId, updateRegistration);

    if (!merchantAccountPaymentsEnabled) {
      clearStartDate();
      clearEndDate();
    }
    closeStepper(true);
  };

  const handleStateChange = (propertyName, keyVal) => {
    try {
      setState((prevState) => ({
        ...prevState,
        [propertyName]: {
          ...prevState[propertyName],
          ...keyVal,
        },
      }));
    } catch (e) {
      console.error(`registration::handleStateChange(): ${e}`);
    }
  };

  const closeStepper = (skipModal = false) => {
    if (!checkForPaymentInformation()) {
      setShowDepositAccountModal(true);
      return;
    }

    if (!skipModal && paymentAccountUnapprovedWithFees) {
      setShowUnapprovedMerchantAccountModal(true);
      return;
    }

    document.getElementById('org')?.classList.remove('formbuilder-lock'); // Need to remove modal-ception class needed for hack
    setIsModalOpen(false);
    setFormId(null);
    setState({
      ...state,
      registration: registrationDefaults,
      fees: formFeesDefaults,
      participantGroups: participantGroupsDefault,
      formFields: formFieldsDefault,
      waivers: waiversDefault,
      message: formMessageDefault,
      dates: 'null - null',
    });
    setStartDate(null);
    setEndDate(null);
    setCurrentStep(0);
    updateCurrentBreadcrumb(Types.RegistrationSteps.PROGRAMS);
  };

  const getSeasonDivisionsTeams = () => {
    let items = selectedSeason?.divisions;
    items = items?.concat(selectedSeason?.teams);
    return items;
  };

  const getCheckedFields = (fields) =>
    fields
      ?.filter((field: Types.FieldType) => field.enabled === true)
      .map((field: Types.FieldType) => field.group || field.label)
      .filter((item, i, ar) => ar.indexOf(item) === i)
      .join(', ') || 'None Added';

  const generateProgramOptions = (programs) => generateEventOptions(programs);

  const generateSeasonOptions = (seasons) => generateEventOptions(seasons);

  const generateEventOptions = (optionData) => {
    try {
      const newData = [];
      optionData?.forEach((option) => {
        const { __typename, name, id } = option;
        const statePortion = __typename?.toLowerCase();

        newData.push({
          type: 'radio',
          name: statePortion,
          text: name,
          id: id,
          selected:
            registration[statePortion]?.value === name &&
            registration[statePortion]?.id === id,
          callback: handleEventChange,
        });
      });

      return newData.sort((a, b) => sortByAlpha(a.text, b.text));
    } catch (e) {
      console.error(`registration::generateEventOptions() for: ${e}`);
    }
  };

  const getProgramSeasons = () => {
    try {
      // Removed Archived seasons and seasons that has Reg1 forms created
      const seasonsNotArchived =
        selectedProgram?.seasons?.filter(({ isArchived }) => !isArchived) || [];
      return seasonsNotArchived.filter((s) => {
        const seasonHasReg1Forms = (form, season) =>
          !form.isReg2 && form.season.id === season.id;
        return !allForms.some((f) => seasonHasReg1Forms(f, s));
      });
    } catch (e) {
      console.error(`registration::getProgramSeasons(): ${e}`);
    }
  };

  const createOrUpdateWaiver = async (newWaiver) => {
    const updatedWaivers = state.waivers.value.map((x) =>
      x.uuid !== newWaiver.uuid ? x : newWaiver
    );

    if (!newWaiver.uuid) {
      updatedWaivers.push({ ...newWaiver, externalId: null, uuid: uuidv4() });
    }

    setState((prevState) => ({
      ...prevState,
      waivers: { ...prevState.waivers, value: updatedWaivers },
    }));
  };

  const handleFormCreationOrUpdate = async (callback = null) => {
    try {
      const variables = {
        organizationId: orgId,
        name: registration.name.value,
        scopeType: 'season',
        seasonId: selectedSeason?.id || registration.season.id,
        paymentAccountId: paymentAccountInfo.paymentAccountId,
        allowOfflineCheckout: paymentAccountInfo.allowOfflineCheckout,
        organizationPaysProcessingFee:
          paymentAccountInfo.organizationPaysProcessingFee,
      };

      if (formId === null) {
        await createFormRequest(variables, createRegistration, (newFormId) => {
          setFormId(newFormId);
          handleUpdateFees(newFormId);
          handleFormFieldsCreationOrUpdate(newFormId);

          if (isAddonsFeatureEnabled) {
            const initialAddonState = availableAddons.map((o) => ({
              enabled: true,
              partnerOfferingId: o.id,
            }));
            setAddonState(initialAddonState);
            handleFormPartnerOfferingsCreateOrUpdate(
              initialAddonState,
              newFormId
            );
          }

          // enable ngb integration automaticaly when is a new form after form has been created
          updateFormNgbIntegrations(newFormId);

          trackChurnZeroEvent('Gemini - Registration form created', org);
        });
      } else {
        let hasSeasonChanged = false;

        if (selectedSeason?.id !== savedForm?.season?.id) {
          hasSeasonChanged = true;
        }

        await updateFormRequest(
          variables,
          formId,
          updateRegistration,
          (updatedFormId) => {
            // enable ngb integration automaticaly when season has changed
            if (hasSeasonChanged) {
              updateFormNgbIntegrations(updatedFormId);
            }
          }
        );
      }

      if (callback) {
        callback();
      }
    } catch (e) {
      console.error(`registration::handleFormCreationOrUpdate(): ${e}`);
    }
  };

  const updateFormNgbIntegrations = (formId) => {
    // perform defaul assignment when there is eligible ngb integration
    if (ngbIntegrationData && ( ngbIntegrationData.code != 'hockey_canada' || isHCRFeatureEnabled)) {
      performDefaultAssignment({
        ngbIntegrationData,
        formId,
        seasonEndDate: selectedSeason?.endDate,
      });
    }

    // disable all ngb integrations for form if not same as eligible
    // ngb integration or if there is no eligible ngb integrations
    if (savedForm?.ngbIntegrations?.length > 0) {
      savedForm.ngbIntegrations.forEach((savedIntegration) => {
        if (savedIntegration.ngbIntegrationId !== ngbIntegrationData?.id) {
          disableSavedNgbIntegrationForForm({
            savedNgbIntegrationData: savedIntegration,
            formId,
          });
        }
      });
    }
  };

  const handleFormPartnerOfferingsCreateOrUpdate = async (
    addonState,
    formId = null
  ) => {
    if (formId && isAddonsFeatureEnabled) {
      try {
        const variables = {
          formId: parseInt(formId),
          partnerOfferings: addonState,
        };
        await handleCreateOrUpdatePartnerOfferingsRequest(
          variables,
          createOrUpdateFormPartnerOfferings
        );
      } catch (e) {
        console.error(
          `registration::handleFormPartnerOfferingsCreateOrUpdate(): ${e}`
        );
      }
    }
  };

  const handleParticipantGroupCreation = async (
    newFormId: number,
    participantGroups: Types.ParticipantGroup[]
  ) => {
    try {
      const variables = {
        formId: newFormId,
        participantGroups,
      };

      await createParticipantGroupRequest(variables, createParticipantGroups);
    } catch (error) {
      console.error(`registration::handleParticipantGroupCreation(): ${error}`);
    }
  };

  const handleParticipantGroupUpdate = async (
    formId: number,
    participantGroups: Types.ParticipantGroupUpdate[]
  ) => {
    try {
      const variables = {
        formId,
        participantGroups,
      };

      await updateParticipantGroupRequest(variables, updateParticipantGroups);
    } catch (error) {
      console.error(`registration::handleParticipantGroupUpdate(): ${error}`);
    }
  };

  const handleParticipantGroupSubmission = (
    formId: number,
    groups: Types.ParticipantGroup[]
  ) => {
    // Filter out objects with duplicate names.
    const uniqueGroups = Array.from(
      new Map(groups.map((group) => [group['name'], group])).values()
    ).map((group) => {
      // Remove expanded and active. It doesn't exist in the call to the backend.
      const { expanded, active, ...rest } = group;

      return rest;
    });

    // Fire any necessary updates
    const groupsNeedingUpdated: Types.ParticipantGroupUpdate[] = updatedGroups
      .filter((g) => !!g.id)
      .map((g) => ({
        id: g.id,
        name: g.name,
        description: g.description,
        participantFee: g.participantFee,
      }));

    if (groupsNeedingUpdated.length > 0) {
      handleParticipantGroupUpdate(formId, groupsNeedingUpdated);
    }

    // Fire any necessary creates
    const newGroups = uniqueGroups.filter((g) => !g.id);
    if (newGroups.length > 0) {
      handleParticipantGroupCreation(formId, newGroups);
    }

    setUpdatedGroups([]); // reset this list to not include them again
  };

  const handleUpdateFees = async (regId = null) => {
    try {
      const feesToUpdate = fees.value.filter((f) => !!f?.id);

      if (feesToUpdate.length > 0) {
        const participantGroups = feesToUpdate.map((fee) => ({
          id: fee.id,
          participantFee: Math.round(parseFloat(fee.amount) * 100) || 0,
        }));

        const variables = {
          formId,
          participantGroups,
        };

        await updateParticipantGroupRequest(variables, updateParticipantGroups);
      }

      if (isAddonsFeatureEnabled) {
        // When the user is removing fees from the registration, check to see if
        // there are offerings that are enabled that require fees and disable them.
        const updatedAddonState = availableAddons.map((o) => {
          let enabled =
            addonState.find((a) => a.partnerOfferingId === o.id).enabled ??
            true;

          if (!hasFees && o.requiresFees) {
            enabled = false;
          }

          return {
            enabled,
            partnerOfferingId: o.id,
          };
        });

        setAddonState(updatedAddonState);
        await handleFormPartnerOfferingsCreateOrUpdate(
          updatedAddonState,
          formId
        );
      }
    } catch (e) {
      console.error(`registration::handleUpdateFees(): ${e}`);
    }
  };

  const handleUpdateGroupEligibility = async () => {
    try {
      if (participantGroups.value.length > 0) {
        const hasAutoRosteringError = eligibilityRules.some(
          (r) => r.active && r.autoRostering.error && r.autoRostering.toggled
        );

        if (hasAutoRosteringError) {
          setEligibilityRules((prevState) => {
            const prevStateCopy = [...prevState];
            return prevStateCopy;
          });
          return;
        }

        const participantGroupsToUpdate = participantGroups.value.map(
          (group) => {
            const foundGroup: Types.GroupEligibilityRow = eligibilityRules.find(
              (r) => r.id === group.id
            );
            return {
              id: group.id,
              from_birthdate_range: !foundGroup.birthdates.toggled
                ? null
                : foundGroup?.birthdates.start || null,
              to_birthdate_range: !foundGroup.birthdates.toggled
                ? null
                : foundGroup?.birthdates.end || null,
              gender_options: !foundGroup.genders.toggled
                ? null
                : foundGroup?.genders.options.length > 0
                ? foundGroup?.genders.options.toString()
                : null,
              roster_scope_type: !foundGroup?.autoRostering?.toggled
                ? null
                : foundGroup?.autoRostering?.rosterScopeType || null,
              roster_scope_id: !foundGroup?.autoRostering?.toggled
                ? null
                : +foundGroup?.autoRostering?.rosterScopeId || null,
              roster_mode: !foundGroup?.autoRostering?.toggled
                ? 'instant'
                : foundGroup?.autoRostering?.rosterMode || 'instant',
            };
          }
        );

        const variables = {
          formId,
          participantGroups: participantGroupsToUpdate,
        };

        await updateParticipantGroupRequest(variables, updateParticipantGroups);
      }
    } catch (e) {
      console.error(`registration::handleUpdateGroupEligibility(): ${e}`);
    }
  };

  const setExpandedCallback = (rowId: number) => {
    // Find the index of the targeted row
    const targetIndex = capacityLimits.findIndex((row) => row.id === rowId);
    // Make a copy since we should not try to change a passed param
    const rowsCopy = [...capacityLimits];

    // rowsCopy[targetIndex].error = null;
    rowsCopy[targetIndex].expanded = !capacityLimits[targetIndex].expanded;

    // Update parent state
    setCapacityLimits(rowsCopy);
  };

  const handleUpdateCapacityLimit = async () => {
    try {
      let errorExists = false;

      if (participantGroups.value.length > 0) {
        if (capacityLimits) {
          // Check if a group has been toggled on but no value was entered, if so
          // Set an error
          capacityLimits.forEach((item: Types.GroupCapacityRow, index) => {
            const itemCopy = { ...item };

            if (item.toggled && !item.maxCapacity && item.maxCapacity !== 0) {
              itemCopy.error =
                'Enter a number or turn capacity limit off for this registration option.';
              itemCopy.expanded = true;
              errorExists = true;

              setCapacityLimits((prevState) => {
                const prevStateCopy = [...prevState];
                prevStateCopy[index] = itemCopy;

                return prevStateCopy;
              });
            }
          });

          if (!errorExists) {
            const participantGroupsToUpdate = participantGroups.value.map(
              (group) => {
                const foundGroup = capacityLimits.find(
                  (r) => r.id === group.id
                );

                return {
                  id: group.id,
                  capacity_limit: foundGroup.maxCapacity,
                  waitlist_allowed: foundGroup.waitlistAllowed,
                };
              }
            );

            const variables = {
              formId,
              participantGroups: participantGroupsToUpdate,
            };

            await updateParticipantGroupRequest(
              variables,
              updateParticipantGroups
            );
          }
        }
      }
    } catch (e) {
      console.error(`registration::handleUpdateCapacityLimit(): ${e}`);
    }
  };

  const handleFormFieldsCreationOrUpdate = async (
    regId = null,
    fields = null
  ) => {
    try {
      await handleFormFieldsRequest(
        fields ? fields.value : formFields.value,
        savedForm?.fields
          ? hydrateFormFields(savedForm.fields, organizationFieldDefinitionList)
          : [],
        formId || regId,
        createFormField,
        updateFormField,
        deleteFormField,
        (fieldsVal) => {
          handleStateChange('formFields', { ...formFields, value: fieldsVal });
        }
      );
    } catch (e) {
      console.error(`registration::handleFormFieldsCreationOrUpdate(): ${e}`);
    }
  };

  const handleWaiversCreateOrUpdate = async () => {
    try {
      await handleFormWaiversRequest(
        waivers.value,
        formId,
        createWaiver,
        updateWaiver,
        createOrUpdateWaiver
      );
    } catch (e) {
      console.error(`registration::handleWaiversCreateOrUpdate(): ${e}`);
    }
  };

  const handleWaiverUpdate = async (waiver: Types.Waiver) => {
    try {
      const variables = { ...waiver, formId };
      await updateWaiver({ variables });
      createOrUpdateWaiver(waiver);
    } catch (e) {
      console.error(`registration::handleWaiverUpdate(): ${e}`);
    }
  };

  const handleFormMessageCreateOrUpdate = async () => {
    try {
      await handleFormMessageRequest(
        message,
        formId,
        createFormMessage,
        updateFormMessage,
        (val) => handleStateChange('message', val)
      );
    } catch (e) {
      console.error(`registration::handleFormMessageCreateOrUpdate(): ${e}`);
    }
  };

  const clearStartDate = () => {
    setStartDate(null);

    if (endDate) {
      setEndDate(null);
    }
  };

  const clearEndDate = () => setEndDate(null);

  const handleStartDateChange = (date: Date) => {
    try {
      const formattedOrgStartTime = date.toLocaleString('en-US');
      setStartDate(date);
      setState((prevState) => ({
        ...prevState,
        dates: `Opens ${formattedOrgStartTime}`,
      }));
    } catch (e) {
      console.error(`registration:handleStartDateChange() -- ${e}`);
    }
  };

  const handleEndDateChange = (date: Date) => {
    try {
      setEndDate(date);
    } catch (e) {
      console.error(`registration:handleEndDateChange() -- ${e}`);
    }
  };

  const handleDateSubmitCallback = async () => {
    try {
      const variables = {
        name: registration.name.value,
        seasonId: selectedSeason?.id,
        openedAt: convertToUTC(startDate, orgTimezoneIana),
        closedAt: convertToUTC(endDate, orgTimezoneIana),
      };

      if (formId) {
        await updateFormRequest(variables, formId, updateRegistration);
      }
    } catch (e) {
      console.error(`registration::handleDateSubmitCallback(): ${e}`);
    }
  };

  const groupFees = (
    <GroupFees
      handleEventChange={(list) =>
        handleStateChange('fees', { ...fees, ...{ value: list } })
      }
      currency={{
        name: org?.currency,
        symbol: org?.currencySymbol,
      }}
      formFees={fees.value}
      formGroups={participantGroups.value}
      isSavedGroupsLoading={
        !savedForm || participantGroups.value.some((group) => !group.id)
      }
    />
  );

  const ConfirmationMessageFormElement = (
    <ConfirmationMessageForm
      message={savedForm?.formMessage?.content || ''}
      saveCallback={(confirmationContent: string) => {
        handleStateChange('message', {
          ...message,
          ...{ content: confirmationContent },
        });
      }}
    />
  );

  const handleUrl = (step: number) => {
    history.replace(`/organizations/${orgId}/registration/${formId}/${step}`);
    updateCurrentBreadcrumb(step);
  };

  const shouldHideChildren = (stepsInSection: Types.RegistrationSteps[]) =>
    !stepsInSection.includes(currentBreadcrumb);

  const canNavigateFreely = () =>
    registration.program.value !== '' &&
    registration.season.value !== '' &&
    registration.name.value !== '' &&
    participantGroups.value.length > 0;

  const isParticipantGroupDisabled = React.useMemo(
    () =>
      registration.program.value === '' ||
      registration.season.value === '' ||
      registration.name.value === '' ||
      !formId,
    [state]
  );

  const checkForPaymentInformation = () => {
    if (hasFees) {
      return !!paymentAccountInfo.paymentAccountId;
    } else {
      return true;
    }
  };

  const showAddons = () => {
    if (!isAddonsFeatureEnabled) {
      return false;
    }

    if (!availableAddons || availableAddons.length === 0) {
      return false;
    }

    if (
      !hasFees &&
      availableAddons?.filter((p) => !p.requiresFees).length === 0
    ) {
      return false;
    }

    return true;
  };

  const handleLeftClickNavigation = () => {
    switch (currentStep) {
      case Types.RegistrationSteps.SCHEDULE:
        handleDateSubmitCallback();
        break;

      case Types.RegistrationSteps.REGISTRATION_FEES:
        handleUpdateFees();
        break;

      case Types.RegistrationSteps.ELIGIBILITY_RULES:
        handleUpdateGroupEligibility();
        break;

      case Types.RegistrationSteps.CAPACITY_LIMIT:
        handleUpdateCapacityLimit();
        break;

      case Types.RegistrationSteps.QUESTIONS_LOGIC:
        handleFormFieldsCreationOrUpdate();
        break;

      case Types.RegistrationSteps.SIGNATURES:
        handleWaiversCreateOrUpdate();
        break;

      case Types.RegistrationSteps.CONFIRMATION_MESSAGE:
        handleFormMessageCreateOrUpdate();
        break;

      case Types.RegistrationSteps.ADDONS:
        handleFormPartnerOfferingsCreateOrUpdate(addonState, formId);
        break;

      case Types.RegistrationSteps.SEASON:
        if (formId) {
          handleFormCreationOrUpdate(); // will update existing forms with the new season selection
          if (selectedSeason?.id !== savedForm?.season?.id) {
            handleUpdateGroupEligibility(); // will reset auto rostering options
          }
        }
        break;

      default:
        handleFormCreationOrUpdate();
        break;
    }
  };

  const handleBreadcrumbStepUpdate = (stepType: Types.RegistrationSteps) => {
    updateCurrentBreadcrumb(stepType);
    setCurrentStep(stepType);
  };

  // START POINT FOR THE REFACTOR
  // NOTE: The date fetch data for the step and its logic will belong to the step itself after the refactor
  const isNgbIntegrationFeatureEnabled =
    FeatureFlagService.isFeatureEnabledForOrg(
      FeatureFlagConfig[FeatureFlags.NGB_INTEGRATIONS],
      ENVIRONMENT,
      Number(orgId)
    );

  const [ngbIntegrationsRawData] = useApiGetNgbIntegrations(
    isNgbIntegrationFeatureEnabled ? selectedSeason?.id ?? null : null
  );
  const ngbIntegrationData = (ngbIntegrationsRawData?.ngbIntegrations || [])[0];
  const savedNgbIntegrationData = React.useMemo(() => {
    if (!savedForm || !ngbIntegrationData) {
      return null;
    }
    return savedForm.ngbIntegrations.find(
      (ngbIntegration) =>
        ngbIntegration.ngbIntegrationId === ngbIntegrationData.id
    );
  }, [ngbIntegrationData, savedForm]);
  const { performDefaultAssignment, disableSavedNgbIntegrationForForm } =
    useNgbIntregrationDefaultAssignment();

  // NOTE: ReactHookForm and Methods that will control all fields validation and submission
  const form = useForm<ReactHookFormInputs>();
  const currentStepSubmitFunction =
    React.useRef<
      (
        handler: StepperSubmitCallbackParams['handler']
      ) => OnSubmit<ReactHookFormInputs>
    >();
  const triggerChangeStep = (stepName: Types.RegistrationSteps) => {
    updateCurrentBreadcrumb(stepName);
    setCurrentStep(stepName);
  };

  const breadcrumb: BreadcrumbSection[] = [
    {
      name: 'General',
      hideChildren: shouldHideChildren([
        ...Types.GENERAL_STEPS,
        Types.RegistrationSteps.NGB_INTEGRATION,
      ]),
      requiredData: Types.VALID_GENERAL_STEPS,
      onClick: () => {
        handleLeftClickNavigation();
        handleBreadcrumbStepUpdate(Types.RegistrationSteps.PROGRAMS);
      },
      children: [
        {
          id: Types.RegistrationSteps.PROGRAMS,
          name: 'Program',
          isValid: registration.program.value !== '',
          onClick: () => {
            handleLeftClickNavigation();
            handleBreadcrumbStepUpdate(Types.RegistrationSteps.PROGRAMS);
          },
        },
        {
          id: Types.RegistrationSteps.SEASON,
          name: 'Season',
          isValid: registration.season.value !== '',
          isDisabled: registration.program.value === '',
          onClick: () => {
            handleLeftClickNavigation();
            handleBreadcrumbStepUpdate(Types.RegistrationSteps.SEASON);
          },
        },
        {
          id: Types.RegistrationSteps.NAME,
          name: 'Name',
          isValid: registration.name.value !== '',
          isDisabled:
            registration.program.value === '' ||
            registration.season.value === '',
          onClick: () => {
            handleLeftClickNavigation();
            handleBreadcrumbStepUpdate(Types.RegistrationSteps.NAME);
          },
        },
        {
          id: Types.RegistrationSteps.SCHEDULE,
          name: 'Schedule',
          isValid: startDate !== null,
          isDisabled:
            !formId ||
            registration.program.value === '' ||
            registration.season.value === '' ||
            registration.name.value === '',
          onClick: () => {
            handleLeftClickNavigation();
            handleBreadcrumbStepUpdate(Types.RegistrationSteps.SCHEDULE);
          },
        },
        {
          id: Types.RegistrationSteps.NGB_INTEGRATION,
          name: ngbIntegrationData
            ? ngbIntegrationData.metadata.formRequirements?.name
            : 'NGB Integration',
          isValid: Boolean(savedNgbIntegrationData),
          isDisabled: !ngbIntegrationData,
          hidden: !ngbIntegrationData || ( ngbIntegrationData.code === 'hockey_canada' && !isHCRFeatureEnabled),
          onClick: () => {
            handleLeftClickNavigation();
            handleBreadcrumbStepUpdate(Types.RegistrationSteps.NGB_INTEGRATION);
          },
        },
      ],
    },
    {
      name: 'Registration Options',
      hideChildren: shouldHideChildren(Types.PARTICIPANT_GROUP_STEPS),
      requiredData: Types.VALID_PARTICIPANT_GROUP_STEPS,
      isDisabled: isParticipantGroupDisabled,
      onClick: () => {
        handleLeftClickNavigation();
        handleBreadcrumbStepUpdate(Types.RegistrationSteps.GROUP_NAMES);
      },
      children: [
        {
          id: Types.RegistrationSteps.GROUP_NAMES,
          name: 'Group Name(s)',
          isValid: participantGroups.value.length > 0,
          onClick: () => {
            handleLeftClickNavigation();
            handleBreadcrumbStepUpdate(Types.RegistrationSteps.GROUP_NAMES);
          },
        },
        {
          id: Types.RegistrationSteps.REGISTRATION_FEES,
          name: 'Registration Fee(s)',
          isValid: fees.value.length > 0,
          isDisabled: participantGroups.value.length === 0,
          onClick: () => {
            handleLeftClickNavigation();
            handleBreadcrumbStepUpdate(
              Types.RegistrationSteps.REGISTRATION_FEES
            );
          },
        },
        {
          id: Types.RegistrationSteps.ELIGIBILITY_RULES,
          name: autoRosteringIsEnabled
            ? 'Eligibility and Rostering'
            : 'Eligibility Rules',
          isValid: fees.value.length > 0,
          hidden: !isParticipantGroupRestrictionEnabled, // TODO: remove this when participant group restriction fully released
          isDisabled: participantGroups.value.length === 0,
          onClick: () => {
            handleLeftClickNavigation();
            handleBreadcrumbStepUpdate(
              Types.RegistrationSteps.ELIGIBILITY_RULES
            );
          },
        },
        {
          id: Types.RegistrationSteps.CAPACITY_LIMIT,
          name: 'Capacity Limit & Waitlist',
          hidden: !isCapacityLimitsEnabled, // TODO: remove this when participant group restriction fully released
          isValid: fees.value.length > 0,
          isDisabled: participantGroups.value.length === 0,
          onClick: () => {
            handleLeftClickNavigation();
            handleBreadcrumbStepUpdate(Types.RegistrationSteps.CAPACITY_LIMIT);
          },
        },
      ],
    },
    {
      name: 'Form Builder',
      hideChildren: shouldHideChildren(Types.FORM_BUILDER_STEPS),
      requiredData: Types.VALID_FORM_BUILDER_STEPS,
      isDisabled: !canNavigateFreely(),
      onClick: () => {
        handleLeftClickNavigation();
        handleBreadcrumbStepUpdate(Types.RegistrationSteps.QUESTIONS_LOGIC);
      },
      children: [
        {
          id: Types.RegistrationSteps.QUESTIONS_LOGIC,
          name: 'Questions & Logic',
          isValid: formFields.value.length > 0,
          onClick: () => {
            handleLeftClickNavigation();
            handleBreadcrumbStepUpdate(Types.RegistrationSteps.QUESTIONS_LOGIC);
          },
        },
        {
          id: Types.RegistrationSteps.SIGNATURES,
          name: 'Digital Agreements',
          isValid: waivers.value.length > 0,
          onClick: () => {
            handleLeftClickNavigation();
            handleBreadcrumbStepUpdate(Types.RegistrationSteps.SIGNATURES);
          },
        },
      ],
    },
    {
      name: 'Checkout Settings',
      requiredData: Types.VALID_CHECKOUT_SETTINGS_STEPS,
      hideChildren: shouldHideChildren(Types.CHECKOUT_SETTINGS_STEPS),
      isDisabled: !canNavigateFreely(),
      hidden: !hasFees,
      onClick: () => {
        handleLeftClickNavigation();
        handleBreadcrumbStepUpdate(Types.RegistrationSteps.DEPOSIT_ACCOUNT);
      },
      children: [
        {
          id: Types.RegistrationSteps.DEPOSIT_ACCOUNT,
          name: 'Deposit Account',
          isValid:
            registration.paymentAccount.value !== '' &&
            registration.paymentAccount.value !== undefined,
          onClick: () => {
            handleLeftClickNavigation();
            handleBreadcrumbStepUpdate(Types.RegistrationSteps.DEPOSIT_ACCOUNT);
          },
        },
        {
          id: Types.RegistrationSteps.INSTALLMENTS,
          name: 'Payments Plan(s)',
          isValid:
            registration.paymentAccount.value !== '' &&
            registration.paymentAccount.value !== undefined,
          onClick: () => {
            handleLeftClickNavigation();
            handleBreadcrumbStepUpdate(Types.RegistrationSteps.INSTALLMENTS);
          },
        },
        {
          id: Types.RegistrationSteps.DISCOUNTS,
          name: 'Discount(s)',
          hidden: !isCouponCodesFeatureEnabled, // TODO: remove this when discounts feature fully released
          isValid:
            registration.paymentAccount.value !== '' &&
            registration.paymentAccount.value !== undefined,
          onClick: () => {
            handleLeftClickNavigation();
            handleBreadcrumbStepUpdate(Types.RegistrationSteps.DISCOUNTS);
          },
        },
      ],
    },
    {
      name: 'Add-ons',
      hideChildren: true,
      isDisabled: !canNavigateFreely(),
      hidden: !showAddons(),
      isValid:
        addonState &&
        addonState.length > 0 &&
        formId !== null &&
        canNavigateFreely(),
      onClick: () => {
        handleLeftClickNavigation();
        handleBreadcrumbStepUpdate(Types.RegistrationSteps.ADDONS);
      },
      children: [
        {
          id: Types.RegistrationSteps.ADDONS,
          name: 'Add-ons',
          isValid: true,
          onClick: () => {
            handleLeftClickNavigation();
            handleBreadcrumbStepUpdate(Types.RegistrationSteps.ADDONS);
          },
        },
      ],
    },
    {
      name: 'Confirmation Message',
      hideChildren: true,
      isDisabled: !canNavigateFreely(),
      isValid: message.id !== null,
      onClick: () => {
        handleLeftClickNavigation();
        handleBreadcrumbStepUpdate(
          Types.RegistrationSteps.CONFIRMATION_MESSAGE
        );
      },
      children: [
        {
          id: Types.RegistrationSteps.CONFIRMATION_MESSAGE,
          name: 'Confirmation Message',
          isValid: message.id !== null,
          onClick: () => {
            handleLeftClickNavigation();
            handleBreadcrumbStepUpdate(
              Types.RegistrationSteps.CONFIRMATION_MESSAGE
            );
          },
        },
      ],
    },
  ];

  return (
    <RegistrationContext.Provider
      value={{
        orgId: Number(orgId),
        formId: Number(formId),
        reactHookForm: form,
        currentStepSubmitFunction,
        savedFormData: savedForm,
        isLoadingSavedFormData,
        triggerChangeStep,
        closeStepper,
      }}
    >
      <div className="create-registration-stepper">
        {showDepositAccountModal && (
          <DepositAccountWarningModal
            orgId={orgId}
            formId={formId}
            show={showDepositAccountModal}
            setCurrentStep={(step) => setCurrentStep(step)}
            onClose={() => setShowDepositAccountModal(false)}
          />
        )}

        {showUnapprovedMerchantAccountModal && (
          <UnapprovedMerchantAccountModal
            isOpen={showUnapprovedMerchantAccountModal}
            paymentsEnabled={merchantAccountPaymentsEnabled}
            closeFn={handleUnapprovedMerchantAccountModalClose}
            onSubmit={handleUnapprovedMerchantAccountModalSubmit}
          />
        )}

        {isModalOpen && (
          <Stepper
            excludeSummary={true}
            handleUrl={handleUrl}
            activeStep={currentStep}
            onClose={closeStepper}
            show={isModalOpen}
            drawer={
              <Breadcrumb
                current={currentBreadcrumb}
                breadcrumb={breadcrumb}
                data={state}
              />
            }
            showProgress={false}
            customStepStyles={{ paddingLeft: '300px' }}
            customHeader={
              <header className="create-registration__header">
                {!registration.program.value ||
                registration.program.value === '' ? (
                  <span className="create-registration__header--default">
                    Create Registration
                  </span>
                ) : (
                  <span>
                    Registering:{' '}
                    {registration.name.value !== ''
                      ? registration.name.value
                      : `${registration.program.value} ${registration.season.value}`}
                  </span>
                )}
              </header>
            }
            currency={{
              name: org?.currency,
              symbol: org?.currencySymbol,
            }}
            steps={[
              {
                title: 'Program',
                value: registration.program.value,
                heading: 'Select program for registration',
                touched: registration.program.touched,
                elements:
                  programsListLoading
                    ? <Loader type="spin" text="Loading programs..." />
                    : programsListError
                    ? <div>Error loading programs</div>
                    : generateProgramOptions(programsList) || [],
                disableNext: registration.program.value === '',
                validation: validationSchema.program,
                stepCallback: () => {
                  updateCurrentBreadcrumb(Types.RegistrationSteps.SEASON);
                  setCurrentStep(Types.RegistrationSteps.SEASON);
                },
                stepBackCallback: () => {
                  closeStepper();
                },
              },
              {
                title: 'Season',
                value: registration.season.value,
                heading: 'Select a season',
                touched: registration.season.touched,
                submitCallback: () => {
                  if (formId) {
                    handleFormCreationOrUpdate(); // will update form with the new season selection
                    if (selectedSeason?.id !== savedForm?.season?.id) {
                      handleUpdateGroupEligibility(); // will reset auto rostering options
                    }
                  }
                },
                elements:
                  getProgramSeasons().length > 0 ? (
                    generateSeasonOptions(getProgramSeasons())
                  ) : (
                    <div>
                      No applicable seasons for this program currently exist.
                    </div>
                  ),
                disableNext: registration.season.value === '',
                validation: validationSchema.season,
                stepCallback: () => {
                  updateCurrentBreadcrumb(Types.RegistrationSteps.NAME);
                  setCurrentStep(Types.RegistrationSteps.NAME);
                },
                stepBackCallback: () => {
                  updateCurrentBreadcrumb(Types.RegistrationSteps.PROGRAMS);
                  setCurrentStep(Types.RegistrationSteps.PROGRAMS);
                },
              },
              {
                title: 'Title',
                value: registration.name.value,
                heading: 'Name this registration',
                touched: registration.name.touched,
                submitCallback: () => {
                  handleFormCreationOrUpdate();
                },
                elements: [
                  {
                    type: 'input',
                    name: 'name',
                    text: registration.name.value,
                    callback: handleEventChange,
                  },
                ],
                validation: validationSchema.name,
                stepCallback: () => {
                  updateCurrentBreadcrumb(Types.RegistrationSteps.SCHEDULE);
                  setCurrentStep(Types.RegistrationSteps.SCHEDULE);
                },
                stepBackCallback: () => {
                  updateCurrentBreadcrumb(Types.RegistrationSteps.SEASON);
                  setCurrentStep(Types.RegistrationSteps.SEASON);
                },
              },
              {
                title: 'Dates',
                value: dates,
                heading: 'Optional: Set registration period',
                subheading:
                  'Set dates to automatically open and close the registration period. If left blank, you will need to set the registration status to open when ready.',
                touched: registration.program.touched,
                elements: (
                  <>
                    <div className="u-size1of1 u-flex u-flexJustifyBetween">
                      <EnhancedDateTimePicker
                        testId="open-date-picker"
                        datetime={startDate}
                        labelText="Open Date"
                        inputClasses="u-size1of2 u-spaceRightMd"
                        onChange={handleStartDateChange}
                        onClear={clearStartDate}
                        timezone={getOrgTimezone(orgTimezoneIana, orgTimezone)}
                        filterTime={excludeCurrentDayPastTimes}
                        openToDate={startDate ? startDate : new Date()}
                        placeholderText="mm / dd / yyyy, -- : -- --"
                      />
                      <EnhancedDateTimePicker
                        testId="close-date-picker"
                        datetime={endDate}
                        labelText="Close Date"
                        inputClasses="u-size1of2 u-spaceLeftMd"
                        onChange={handleEndDateChange}
                        onClear={clearEndDate}
                        timezone={getOrgTimezone(orgTimezoneIana, orgTimezone)}
                        filterTime={(dateTime) =>
                          excludeStartDatePastTimes(dateTime, startDate)
                        }
                        disabled={!startDate}
                        startDate={
                          startDate
                            ? excludeDaysUntilStartDate(startDate)
                            : undefined
                        }
                        openToDate={
                          startDate
                            ? excludeDaysUntilStartDate(startDate)
                            : undefined
                        }
                        placeholderText="mm / dd / yyyy, -- : -- --"
                      />
                    </div>
                  </>
                ),
                submitCallback: () => {
                  handleDateSubmitCallback();
                },
                stepCallback: () => {
                  if(ngbIntegrationData && ( ngbIntegrationData.code === 'hockey_canada' && !isHCRFeatureEnabled) ) {
                    triggerChangeStep(Types.RegistrationSteps.GROUP_NAMES);
                  }
                  else if (ngbIntegrationData) {
                    triggerChangeStep(Types.RegistrationSteps.NGB_INTEGRATION);
                  } else {
                    triggerChangeStep(Types.RegistrationSteps.GROUP_NAMES);
                  }
                },
                stepBackCallback: () => {
                  updateCurrentBreadcrumb(Types.RegistrationSteps.NAME);
                  setCurrentStep(Types.RegistrationSteps.NAME);
                },
              },
              {
                title: ngbIntegrationData
                  ? ngbIntegrationData.metadata?.formRequirements?.title
                  : 'NGB Integration',
                heading: ngbIntegrationData ? (
                  <div className="sui-flex sui-items-center sui-gap-1">
                    {ngbIntegrationData.metadata?.formRequirements?.title}
                    <Tooltip
                      side="right"
                      align="center"
                      content={
                        ngbIntegrationData.metadata.formRequirements
                          ?.tooltipText
                      }
                    >
                      <Icon name="info" className="sui-text-action-icon" />
                    </Tooltip>
                  </div>
                ) : (
                  ''
                ),
                subheading: ngbIntegrationData
                  ? ngbIntegrationData.metadata.formRequirements?.subtitle
                  : '',
                elements: (
                  <NgbIntegrationStep
                    /*
                      NOTE: Passing the raw data because after the refactor
                      each step will be responsible for its own data
                    */
                    ngbIntegrationsRawData={ngbIntegrationsRawData}
                  />
                ),
                /*
                  NOTE: Now the submitCallback will be the only way to handle
                  the form submission and its behaviour after it.
                */
                legacy_handleNextStepDefaultBehavior: false,
                legacy_handleBackStepDefaultBehavior: false,
                legacy_handleSaveAndExitStepperDefaultBehavior: false,
                submitCallback: async ({
                  handler,
                }: StepperSubmitCallbackParams) => {
                  if (currentStepSubmitFunction?.current) {
                    const submitWithHandler =
                      currentStepSubmitFunction.current(handler);
                    await form.handleSubmit(submitWithHandler)();
                  }
                },
              },
              {
                title: 'Registration Options',
                heading: 'Registration Options',
                subheading:
                  'Add or create the registration options (e.g. divisions, activities, etc) that will be available to your registrants.',
                value: formatGroupsForSummary(participantGroups.value),
                elements: (
                  <ParticipantGroups
                    handleStateChange={handleStateChange}
                    currentState={state}
                    formId={formId}
                    orgId={orgId}
                    setUpdatedGroups={setUpdatedGroups}
                    divisionsTeams={getSeasonDivisionsTeams()}
                  />
                ),
                touched: participantGroups.touched,
                validation: validationSchema.participantGroups,
                disableNext: participantGroups.value.length === 0,
                submitCallback: () => {
                  handleFormCreationOrUpdate();
                },
                stepCallback: () => {
                  updateCurrentBreadcrumb(
                    Types.RegistrationSteps.REGISTRATION_FEES
                  );
                  setCurrentStep(Types.RegistrationSteps.REGISTRATION_FEES);
                },
                stepBackCallback: () => {
                  if(ngbIntegrationData && ( ngbIntegrationData.code === 'hockey_canada' && !isHCRFeatureEnabled)) {
                    triggerChangeStep(Types.RegistrationSteps.SCHEDULE);
                  }
                  else if (ngbIntegrationData) {
                    triggerChangeStep(Types.RegistrationSteps.NGB_INTEGRATION);
                  } else {
                    triggerChangeStep(Types.RegistrationSteps.SCHEDULE);
                  }
                },
              },
              {
                title: 'Fees',
                value: formatFeesForSummary(fees.value),
                heading: 'Add registration fees',
                subheading:
                  'Enter the base fee for each option. Additional fees (e.g. equipment, uniform, travel, etc) can be added at a later stage.',
                touched: fees.touched,
                disableNext: fees.value.some(
                  (fee) => parseFloat(fee.amount) < 0
                ),
                submitCallback: () => {
                  handleUpdateFees();
                },
                elements: groupFees,
                validation: validationSchema.fees,
                stepCallback: () => {
                  // TODO: remove if statement when participant group restriction fully released
                  if (isParticipantGroupRestrictionEnabled) {
                    updateCurrentBreadcrumb(
                      Types.RegistrationSteps.ELIGIBILITY_RULES
                    );
                    setCurrentStep(Types.RegistrationSteps.ELIGIBILITY_RULES);
                  } else {
                    updateCurrentBreadcrumb(
                      Types.RegistrationSteps.QUESTIONS_LOGIC
                    );
                    setCurrentStep(Types.RegistrationSteps.QUESTIONS_LOGIC);
                  }
                },
                stepBackCallback: () => {
                  updateCurrentBreadcrumb(Types.RegistrationSteps.GROUP_NAMES);
                  setCurrentStep(Types.RegistrationSteps.GROUP_NAMES);
                },
              },
              {
                title: autoRosteringIsEnabled
                  ? 'Eligibility and Rostering'
                  : 'Eligibility Rules',
                value: null,
                heading: 'Who can register for each group?',
                subheading:
                  'Please define who can register for each group. Only eligible groups will be displayed for each participant to register for.',
                preventNext: eligibilityRules?.some(
                  (item) =>
                    item.active &&
                    item.autoRostering.error &&
                    item.autoRostering.toggled
                ),
                submitCallback: () => {
                  handleUpdateGroupEligibility();
                },
                elements: (
                  <GroupEligibility
                    genders={
                      organizationFieldDefinitionList?.find(
                        (f) => f.name === 'gender' && !f.organization_id
                      )?.options ?? []
                    }
                    rows={eligibilityRules}
                    setRowsCallback={setEligibilityRules}
                    orgId={orgId}
                  />
                ),
                stepCallback: () => {
                  if (isCapacityLimitsEnabled) {
                    updateCurrentBreadcrumb(
                      Types.RegistrationSteps.CAPACITY_LIMIT
                    );
                    setCurrentStep(Types.RegistrationSteps.CAPACITY_LIMIT);
                  } else {
                    updateCurrentBreadcrumb(
                      Types.RegistrationSteps.QUESTIONS_LOGIC
                    );
                    setCurrentStep(Types.RegistrationSteps.QUESTIONS_LOGIC);
                  }
                },
                stepBackCallback: () => {
                  updateCurrentBreadcrumb(
                    Types.RegistrationSteps.REGISTRATION_FEES
                  );
                  setCurrentStep(Types.RegistrationSteps.REGISTRATION_FEES);
                },
              },
              {
                title: 'Capacity Limit',
                value: null,
                heading: 'Manage capacity limit',
                touched: groupCapacity.touched,
                preventNext: capacityLimits?.some(
                  (item) =>
                    item.toggled && !item.maxCapacity && item.maxCapacity !== 0
                ),
                subheading: 'Select the desired capacity limit and waitlist settings.',
                submitCallback: () => {
                  handleUpdateCapacityLimit();
                },
                elements: (
                  <GroupCapacity
                    orgId={orgId}
                    rows={capacityLimits}
                    setRowsCallback={setCapacityLimits}
                    setExpandedCallback={setExpandedCallback}
                  />
                ),
                stepCallback: () => {
                  updateCurrentBreadcrumb(
                    Types.RegistrationSteps.QUESTIONS_LOGIC
                  );
                  setCurrentStep(Types.RegistrationSteps.QUESTIONS_LOGIC);
                },
                stepBackCallback: () => {
                  updateCurrentBreadcrumb(
                    Types.RegistrationSteps.ELIGIBILITY_RULES
                  );
                  setCurrentStep(Types.RegistrationSteps.ELIGIBILITY_RULES);
                },
              },
              {
                title: 'Questions & Logic',
                heading: 'Manage questions',
                subheading:
                  'Customize your registration questions to collect the information you need from your participants.',
                value: getCheckedFields(formFields?.value),
                elements: (
                  <Questions
                    orgId={orgId}
                    formId={formId}
                    fields={formFields.value}
                    organizationDefinitions={organizationFieldDefinitionList}
                    /*  on the initial form creation all offerings/addons get set to enabled. If there are no reg fees,
                      we would hide the partner offerings that have requiredFees=true from the toggle screen even
                      though they would still have a record in the form_partner_offerings as enabled. There could be
                      situations where a registration has an offering enabled (from a data/table perspective), but
                      has no reg fees and this filtering prevents those hidden offerings from locking specific fields.
                  */
                    addonsEnabled={
                      addonState?.some(
                        (a) =>
                          a.enabled &&
                          availableAddons
                            .filter(
                              (a) =>
                                (a.requiresFees && hasFees) || !a.requiresFees
                            )
                            .map((a) => a.id)
                            .includes(a.partnerOfferingId)
                      ) ?? false
                    }
                    onUpdate={(newFields) => {
                      if (newFields.length > 0) {
                        handleFormFieldsCreationOrUpdate(formId, {
                          ...formFields,
                          ...{ value: newFields },
                        });
                      }
                    }}
                  />
                ),
                touched: formFields.touched,
                submitCallback: () => {
                  handleFormFieldsCreationOrUpdate();
                },
                validation: validationSchema.participants,
                stepCallback: () => {
                  updateCurrentBreadcrumb(Types.RegistrationSteps.SIGNATURES);
                  setCurrentStep(Types.RegistrationSteps.SIGNATURES);
                },
                stepBackCallback: () => {
                  // TODO: remove if statement when participant group and capacity limit restriction fully released
                  if (isCapacityLimitsEnabled) {
                    updateCurrentBreadcrumb(
                      Types.RegistrationSteps.CAPACITY_LIMIT
                    );
                    setCurrentStep(Types.RegistrationSteps.CAPACITY_LIMIT);
                  } else if (
                    !isCapacityLimitsEnabled &&
                    isParticipantGroupRestrictionEnabled
                  ) {
                    updateCurrentBreadcrumb(
                      Types.RegistrationSteps.ELIGIBILITY_RULES
                    );
                    setCurrentStep(Types.RegistrationSteps.ELIGIBILITY_RULES);
                  } else {
                    updateCurrentBreadcrumb(
                      Types.RegistrationSteps.REGISTRATION_FEES
                    );
                    setCurrentStep(Types.RegistrationSteps.REGISTRATION_FEES);
                  }
                },
              },
              {
                title: 'Digital Agreements',
                value:
                  waivers.value.length > 0
                    ? waivers.value.map((waiver) => waiver.title).join(', ')
                    : 'None Added',
                heading: 'Manage Digital Agreements',
                subheading:
                  'Registrants will be asked to read and approve all digital agreements.',
                touched: waivers.touched,
                elements: (
                  <WaiverForm
                    formId={formId}
                    waivers={waivers.value}
                    ngbIntegration={savedForm?.ngbIntegrations[0]}
                    setWaivers={(newWaivers) =>
                      handleStateChange('waivers', {
                        ...waivers,
                        ...{ value: newWaivers },
                      })
                    }
                    handleWaiverSave={createOrUpdateWaiver}
                    handleWaiverUpdate={handleWaiverUpdate}
                  />
                ),
                validation: validationSchema.waivers,
                stepCallback: () => {
                  if (hasFees) {
                    updateCurrentBreadcrumb(
                      Types.RegistrationSteps.DEPOSIT_ACCOUNT
                    );
                    setCurrentStep(Types.RegistrationSteps.DEPOSIT_ACCOUNT);
                  } else if (showAddons()) {
                    updateCurrentBreadcrumb(Types.RegistrationSteps.ADDONS);
                    setCurrentStep(Types.RegistrationSteps.ADDONS);
                  } else {
                    updateCurrentBreadcrumb(
                      Types.RegistrationSteps.CONFIRMATION_MESSAGE
                    );
                    setCurrentStep(
                      Types.RegistrationSteps.CONFIRMATION_MESSAGE
                    );
                  }
                },
                stepBackCallback: () => {
                  updateCurrentBreadcrumb(
                    Types.RegistrationSteps.QUESTIONS_LOGIC
                  );
                  setCurrentStep(Types.RegistrationSteps.QUESTIONS_LOGIC);
                },
              },
              {
                title: 'Merchant Account',
                value: registration.paymentAccount.value,
                heading: 'Select deposit account',
                subheading:
                  'Fees collected for this form will be deposited to the selected account.',
                touched: registration.paymentAccount.touched,
                submitCallback: () => {
                  handleFormCreationOrUpdate();
                },
                elements: (
                  <MerchantAccountSelect
                    orgId={orgId}
                    info={paymentAccountInfo}
                    onChange={setPaymentAccountInfo}
                    accounts={paymentAccounts}
                    formId={formId}
                  />
                ),
                validation: validationSchema.paymentAccount,
                hideIfBlank: true,
                stepCallback: () => {
                  updateCurrentBreadcrumb(Types.RegistrationSteps.INSTALLMENTS);
                  setCurrentStep(Types.RegistrationSteps.INSTALLMENTS);
                },
                stepBackCallback: () => {
                  updateCurrentBreadcrumb(Types.RegistrationSteps.SIGNATURES);
                  setCurrentStep(Types.RegistrationSteps.SIGNATURES);
                },
              },
              {
                title: 'Installments',
                heading: 'Create Payment Plan(s)',
                elements: (
                  <PaymentPlans
                    scopeId={formId}
                    scopeType="form"
                    orgId={orgId}
                    currencySymbol={org?.currencySymbol}
                    currencyCode={org?.currency}
                  />
                ),
                submitCallback: () => {
                  handleFormCreationOrUpdate();
                },
                stepCallback: () => {
                  if (isCouponCodesFeatureEnabled) {
                    updateCurrentBreadcrumb(Types.RegistrationSteps.DISCOUNTS);
                    setCurrentStep(Types.RegistrationSteps.DISCOUNTS);
                  } else {
                    updateCurrentBreadcrumb(
                      Types.RegistrationSteps.CONFIRMATION_MESSAGE
                    );
                    setCurrentStep(
                      Types.RegistrationSteps.CONFIRMATION_MESSAGE
                    );
                  }
                },
                stepBackCallback: () => {
                  updateCurrentBreadcrumb(
                    Types.RegistrationSteps.DEPOSIT_ACCOUNT
                  );
                  setCurrentStep(Types.RegistrationSteps.DEPOSIT_ACCOUNT);
                },
              },
              {
                title: 'Discounts',
                heading: 'Create discount(s)',
                elements: (
                  <Discounts
                    formId={formId}
                    orgId={orgId}
                    adjustments={formAdjustments}
                  />
                ),
                submitCallback: () => {
                  handleFormCreationOrUpdate();
                },
                stepCallback: () => {
                  if (showAddons()) {
                    updateCurrentBreadcrumb(Types.RegistrationSteps.ADDONS);
                    setCurrentStep(Types.RegistrationSteps.ADDONS);
                  } else {
                    updateCurrentBreadcrumb(
                      Types.RegistrationSteps.CONFIRMATION_MESSAGE
                    );
                    setCurrentStep(
                      Types.RegistrationSteps.CONFIRMATION_MESSAGE
                    );
                  }
                },
                stepBackCallback: () => {
                  updateCurrentBreadcrumb(Types.RegistrationSteps.INSTALLMENTS);
                  setCurrentStep(Types.RegistrationSteps.INSTALLMENTS);
                },
              },
              {
                title: 'Add-ons',
                heading: 'Add-ons',
                elements: (
                  <Addons
                    addonState={addonState}
                    availableAddons={availableAddons}
                    hasRegistrationFee={hasFees}
                    onAddonUpdate={(state) => {
                      setAddonState(state);
                    }}
                  />
                ),
                submitCallback: () => {
                  handleFormPartnerOfferingsCreateOrUpdate(addonState, formId);
                },
                stepCallback: () => {
                  updateCurrentBreadcrumb(
                    Types.RegistrationSteps.CONFIRMATION_MESSAGE
                  );
                  setCurrentStep(Types.RegistrationSteps.CONFIRMATION_MESSAGE);
                },
                stepBackCallback: () => {
                  if (hasFees) {
                    if (isCouponCodesFeatureEnabled) {
                      updateCurrentBreadcrumb(
                        Types.RegistrationSteps.DISCOUNTS
                      );
                      setCurrentStep(Types.RegistrationSteps.DISCOUNTS);
                    } else {
                      updateCurrentBreadcrumb(
                        Types.RegistrationSteps.INSTALLMENTS
                      );
                      setCurrentStep(Types.RegistrationSteps.INSTALLMENTS);
                    }
                  } else {
                    updateCurrentBreadcrumb(Types.RegistrationSteps.SIGNATURES);
                    setCurrentStep(Types.RegistrationSteps.SIGNATURES);
                  }
                },
              },
              {
                title: 'Confirmation',
                value: message.content ? 'Custom' : 'Standard',
                heading: 'Create a registration confirmation message',
                subheading:
                  'Enter any final instructions, additional information, or next steps to be displayed on the registration confirmation.',
                touched: message.touched,
                elements: savedForm ? (
                  ConfirmationMessageFormElement
                ) : (
                  <Loader type="spin" text="Loading..." />
                ),
                submitCallback: handleFormMessageCreateOrUpdate,
                customNextButtonText: 'Done',
                onStepFocus: () => {
                  handleStateChange('message', {
                    ...message,
                    touched: true,
                  });
                },
                stepBackCallback: () => {
                  if (
                    isAddonsFeatureEnabled &&
                    availableAddons &&
                    availableAddons.length > 0
                  ) {
                    updateCurrentBreadcrumb(Types.RegistrationSteps.ADDONS);
                    setCurrentStep(Types.RegistrationSteps.ADDONS);
                  } else if (hasFees && isCouponCodesFeatureEnabled) {
                    updateCurrentBreadcrumb(Types.RegistrationSteps.DISCOUNTS);
                    setCurrentStep(Types.RegistrationSteps.DISCOUNTS);
                  } else if (hasFees) {
                    updateCurrentBreadcrumb(
                      Types.RegistrationSteps.INSTALLMENTS
                    );
                    setCurrentStep(Types.RegistrationSteps.INSTALLMENTS);
                  } else {
                    updateCurrentBreadcrumb(Types.RegistrationSteps.SIGNATURES);
                    setCurrentStep(Types.RegistrationSteps.SIGNATURES);
                  }
                },
              },
            ].filter((step) => step != null)}
          />
        )}
      </div>
    </RegistrationContext.Provider>
  );
};

export default Registration;
